// components/industry-templates/BankingEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building, TrendingUp, Shield, CreditCard } from 'lucide-react';

export const BankingEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Banking Solutions */}
      {industryData?.banking_solutions && (
        <Card className="border-0 bg-gradient-to-br from-blue-600/20 to-indigo-600/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Building className="w-8 h-8" style={{ color: theme.primary }} />
              Banking & Financial Solutions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.banking_solutions.map((solution: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">💳</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{solution.name}</h3>
                      <p className="text-slate-300 text-sm mb-3">{solution.description}</p>
                      <div className="flex gap-2">
                        <Badge style={{ backgroundColor: theme.primary }}>{solution.category}</Badge>
                        {solution.demo_available && (
                          <Badge className="bg-green-500">Live Demo</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fintech Innovations */}
      {industryData?.fintech_showcase && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-6 h-6" style={{ color: theme.primary }} />
              FinTech Innovation Showcase
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                  {industryData.fintech_showcase.startups}+
                </p>
                <p className="text-sm text-slate-400">FinTech Startups</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                  {industryData.fintech_showcase.demos}
                </p>
                <p className="text-sm text-slate-400">Product Demos</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                  {industryData.fintech_showcase.pitch_sessions}
                </p>
                <p className="text-sm text-slate-400">Pitch Sessions</p>
              </div>
            </div>
            {industryData.fintech_showcase.categories && (
              <div className="flex flex-wrap gap-2">
                {industryData.fintech_showcase.categories.map((cat: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="border-white/20 text-white">
                    {cat}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Regulatory Sessions */}
      {industryData?.regulatory_sessions && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-6 h-6" style={{ color: theme.primary }} />
              Regulatory & Compliance Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.regulatory_sessions.map((session: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-1">{session.topic}</h4>
                  <p className="text-sm text-slate-400 mb-2">{session.speaker}</p>
                  <Badge style={{ backgroundColor: theme.primary }}>{session.time}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Digital Banking */}
      {industryData?.digital_banking && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-6 h-6" style={{ color: theme.primary }} />
              Digital Banking Experience Zone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white mb-4">Experience the future of digital banking with:</p>
              <div className="grid grid-cols-2 gap-3">
                {industryData.digital_banking.features?.map((feature: string, idx: number) => (
                  <div key={idx} className="p-3 bg-white/5 rounded-lg flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
